import React, { useState } from "react";

// Material UI
import { makeStyles } from "@mui/styles";
import {
    Box,
    Chip,
    ListItemSecondaryAction,
    Typography,
} from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// My Files
import { commaSeperatedStringFromLabels, timeString } from "../../utility";

export function durationForClip(clip) {
    const duration =
        clip.type === "annotation"
            ? Number(clip.duration ?? 5)
            : Number(clip.outTime) - Number(clip.inTime);

    return Number.isFinite(duration) ? Math.max(0, duration) : 0;
}

const useStyles = makeStyles((theme) => ({
    listItem: {
        // minHeight: 500,
        // paddingLeft: 50,
    },
    reorderUpButton: {
        position: "absolute",
        top: 0,
        padding: 5,
    },
    reorderDownButton: {
        position: "absolute",
        bottom: 0,
        padding: 5,
        margin: 0,
    },
    normalPadding: {
        minWidth: 15,
        width: 15,
    },
    reorderPadding: {
        minWidth: 35,
        width: 35,
    },
}));

export default function PlaylistListItem(props) {
    const classes = useStyles();

    const isAnnotation = props.clip.type === "annotation";
    const duration = timeString(durationForClip(props.clip));

    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    function notesAndLabelsText(clip) {
        var text = "";

        if (clip.labels) {
            text += commaSeperatedStringFromLabels(clip.labels);
        }

        if (clip.note && clip.note !== "") {
            text += " (" + clip.note + ")";
        }

        return text;
    }

    function handleUpwardButtonPressed() {
        props.handleMoveClipUpward(props.clip);
    }

    function handleDownwardButtonPressed() {
        props.handleMoveClipDownward(props.clip);
    }

    function handleClipSelect(e) {
        if (!props.isReorderingClips) {
            props.handleClipSelect(props.index, props.clip, e);
        }
    }

    const handleMenuClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event) => {
        if (event) {
            event.stopPropagation();
        }
        setAnchorEl(null);
    };

    const handleEdit = (event) => {
        event.stopPropagation();
        handleMenuClose();
        if (props.onEditAnnotation) {
            props.onEditAnnotation(props.clip, props.clipKey);
        }
    };

    const handleDelete = (event) => {
        event.stopPropagation();
        handleMenuClose();
        if (props.onDeleteAnnotation) {
            props.onDeleteAnnotation(props.clip, props.clipKey);
        }
    };

    // Determine styling based on current clip and fullscreen mode
    const getListItemSx = () => {
        if (props.currentClip === props.clip) {
            return {
                backgroundColor: "primary.main",
                color: "white",
                borderColor: "primary.main",
                "&:hover": {
                    backgroundColor: "primary.dark",
                },
            };
        }

        return {
            backgroundColor: props.isFullscreen
                ? "rgb(60, 60, 60)"
                : "background.subtle",
            color: props.isFullscreen
                ? "rgba(255, 255, 255, 0.9)"
                : "text.primary",
            borderColor: props.isFullscreen
                ? "rgba(255, 255, 255, 0.12)"
                : "divider",
            "&:hover": {
                backgroundColor: props.isFullscreen
                    ? "rgb(72, 72, 72)"
                    : "action.hover",
            },
        };
    };

    return (
        <div>
            <ListItem
                className={classes.listItem}
                button
                onClick={handleClipSelect}
                sx={{
                    minHeight: 72,
                    p: 0,
                    m: 0,
                    border: "1px solid",
                    borderRadius: 2,
                    overflow: "hidden",
                    transition:
                        "background-color 120ms ease, border-color 120ms ease",
                    ...getListItemSx(),
                }}
            >
                {props.isReorderingClips ? (
                    <div>
                        <div className={classes.reorderPadding}></div>
                        <IconButton
                            className={classes.reorderUpButton}
                            component="span"
                            onClick={handleUpwardButtonPressed}
                            size="large"
                        >
                            <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton
                            className={classes.reorderDownButton}
                            component="span"
                            onClick={handleDownwardButtonPressed}
                            size="large"
                        >
                            <ArrowDownwardIcon />
                        </IconButton>
                    </div>
                ) : (
                    <div className={classes.normalPadding}></div>
                )}
                <ListItemText
                    primary={
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                            }}
                        >
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{ fontWeight: 750 }}
                            >
                                {props.index + 1}. {props.clip.name}
                            </Typography>
                            {isAnnotation && (
                                <Chip
                                    label="Annotation"
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: 11,
                                        color: "inherit",
                                    }}
                                />
                            )}
                        </Box>
                    }
                    primaryTypographyProps={{
                        style: {
                            paddingRight: 75,
                            overflow: "hidden",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            display: "-webkit-box",
                        },
                    }}
                    secondary={!isAnnotation && notesAndLabelsText(props.clip)}
                    secondaryTypographyProps={{
                        style: {
                            paddingRight: 100,
                            overflow: "hidden",
                            WebkitBoxOrient: "vertical",
                            display: "-webkit-box",
                            textAlign: "justify",
                            textJustify: "inter-word",
                        },
                    }}
                />
                <ListItemSecondaryAction
                    style={
                        props.currentClip === props.clip || props.isFullscreen
                            ? {
                                  color: "white",
                              }
                            : {}
                    }
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                            variant="caption"
                            component="span"
                            aria-label={`Duration ${duration}`}
                            sx={{
                                color:
                                    props.currentClip === props.clip ||
                                    props.isFullscreen
                                        ? "inherit"
                                        : "text.secondary",
                            }}
                        >
                            {duration}
                        </Typography>
                        {isAnnotation && !props.isFullscreen && (
                            <IconButton
                                size="small"
                                onClick={handleMenuClick}
                                style={
                                    props.currentClip === props.clip
                                        ? { color: "white" }
                                        : {}
                                }
                            >
                                <MoreVertIcon />
                            </IconButton>
                        )}
                    </Box>
                </ListItemSecondaryAction>
            </ListItem>
            {isAnnotation && (
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuItem onClick={handleEdit}>
                        <ListItemIcon>
                            <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        Edit annotation
                    </MenuItem>
                    <MenuItem
                        onClick={handleDelete}
                        sx={{ color: "error.main" }}
                    >
                        <ListItemIcon sx={{ color: "error.main" }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        Delete annotation
                    </MenuItem>
                </Menu>
            )}
        </div>
    );
}
